/*
 * A cart line's size, changed in place (snippets/cart-line-size.liquid), in the drawer and on the
 * cart page. Shopify cannot change a line's variant, so a new size is added first and the old line
 * removed by its key through the line's own CartItems.updateQuantity, which redraws the drawer or
 * the page as any quantity change does. Adding first means a failed add leaves the old line; any
 * failure redraws the cart from Shopify, so the shopper never sees a cart checkout does not have.
 */
if (!customElements.get("cart-line-size")) {
  class CartLineSize extends HTMLElement {
    connectedCallback() {
      // Bound once: moving the element in the page connects it again.
      if (this.bound) return;
      this.toggle = this.querySelector("[data-cart-size-toggle]");
      this.list = this.querySelector("[data-cart-size-list]");
      if (!this.toggle || !this.list) return;
      this.bound = true;
      this.toggle.addEventListener("click", () => this.setOpen(this.list.hidden));
      this.list.addEventListener("click", (event) => {
        const size = event.target.closest("button[data-variant-id]");
        if (size) this.choose(size, event);
      });
      // Escape closes the list before it closes the drawer, which listens on keyup.
      this.addEventListener("keyup", (event) => {
        if (event.key !== "Escape" || this.list.hidden) return;
        event.stopPropagation();
        this.setOpen(false);
        this.toggle.focus();
      });
    }

    setOpen(open) {
      this.list.hidden = !open;
      this.toggle.setAttribute("aria-expanded", String(open));
    }

    get host() {
      return this.closest("cart-drawer-items") || this.closest("cart-items");
    }

    get busy() {
      // A quantity change or another size change still on its way targets lines by position.
      return Boolean(
        CartLineSize.inFlight ||
          this.host?.querySelector(".loading__spinner:not(.hidden)"),
      );
    }

    async choose(size, event) {
      const host = this.host;
      if (!host || this.busy) return;
      const line = Number(this.dataset.index);
      const key = this.dataset.key;
      const quantity = Number(this.dataset.quantity) || 1;
      const oldVariantId = Number(this.dataset.variantId);
      const variantId = Number(size.dataset.variantId);
      const properties = CartLineSize.hash(this.dataset.properties);

      CartLineSize.inFlight = true;
      globalThis.UMITbyb?.cartMutationStart();
      try {
        host.enableLoading(line);
        // The JSON endpoint: a JSON body posted to plain /cart/add is refused with a 400.
        const added = await fetch(`${window.routes.cart_add_url}.js`, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
        });
        if (!added.ok) {
          const failure = await added.json().catch(() => ({}));
          throw Object.assign(new Error(failure.description || failure.message || ""), {
            status: added.status,
          });
        }
        try {
          await host.updateQuantity(line, 0, event, undefined, variantId, key);
        } catch (error) {
          // A key changes when the cart's discounts change; find the old line again, once.
          if (error.status !== 400) throw error;
          const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
          const same = JSON.stringify(properties);
          const old = cart.items.find(
            (item) =>
              item.variant_id === oldVariantId &&
              JSON.stringify(CartLineSize.hash(item.properties)) === same,
          );
          if (!old) throw error;
          await host.updateQuantity(line, 0, event, undefined, variantId, old.key);
        }
        // Back to the changed line only for the keyboard (a click reports detail 0 when it comes
        // from Enter or Space): moved by script after a mouse click, focus would draw its ring.
        if (event?.detail === 0) CartLineSize.focusLine(variantId);
      } catch (error) {
        // Redraw first, then say why: the redraw replaces the element that holds the message.
        await CartLineSize.redraw(host);
        CartLineSize.showError(host, error.status ? error.message : "");
      } finally {
        host.disableLoading(line);
        globalThis.UMITbyb?.cartMutationEnd();
        CartLineSize.inFlight = false;
      }
    }

    // Line properties as Shopify accepts them: a hash. Liquid prints a line without properties as
    // [] and /cart.js may send null; an array is refused ("expected Array to be a Hash").
    static hash(value) {
      let parsed = value;
      if (typeof value === "string") {
        try {
          parsed = JSON.parse(value || "{}");
        } catch (error) {
          parsed = {};
        }
      }
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    }

    static focusLine(variantId) {
      const toggle = document.querySelector(
        `cart-drawer-items [data-cart-size-variant="${variantId}"], cart-items [data-cart-size-variant="${variantId}"]`,
      );
      if (toggle) toggle.focus();
    }

    // Only Shopify's own reasons are shown (they come translated); anything else, such as a
    // network failure, gets the store's error.
    static showError(host, message) {
      const errors = document.getElementById(
        host.tagName === "CART-DRAWER-ITEMS" ? "CartDrawer-CartErrors" : "cart-errors",
      );
      if (errors) errors.textContent = message || window.cartStrings?.error || "";
    }

    static async redraw(host) {
      try {
        await host.onCartUpdate?.();
      } catch (error) {
        // The message below still tells the shopper something went wrong.
      }
    }
  }

  customElements.define("cart-line-size", CartLineSize);
}
